/**
 * @module TerrainHelpers
 * Helper functions for terrain/map related operations
 */
import * as THREE from "three";
import { RENDER_CONSTANTS } from "@/helpers/GameConstants";
import {
  setCastShadow,
  setReceiveShadow,
  isGrass
} from "@/helpers/ThreeHelpers";
import { LAYERS } from "@/three/constants";

/**
 * Removes all objects instanced with blender, converting them to instanced meshes
 * @param {*} scene
 * @param {*} objects
 * @returns
 */
export const optimizeInstancedObjects = (scene, objects) => {
  if (objects.length === 0) {
    return;
  }
  const firstInstance = objects[0];
  // Get the geometry and material from the first grass billboard instance
  const sourceMeshes = [];

  function addSourceMeshes(object) {
    //If the first instance is a group, get the geometries and materials from the children
    if (object.isGroup) {
      object.children.forEach((child) => {
        addSourceMeshes(child);
      });
    } else if (object.isMesh) {
      // https://discourse.threejs.org/t/instancedmesh-render-order-issue/56129
      object.material.depthWrite = true;
      object.material.alphaTest = 0.9;
      sourceMeshes.push({
        geometry: object.geometry.clone(),
        material: object.material.clone()
      });
    } else {
      console.error("Unexpected instanced object type, ", object);
      return;
    }
  }
  addSourceMeshes(firstInstance);

  // Create an instanced geometry for the objects
  const instancedObjects = sourceMeshes.map(
    (sourceMesh) =>
      new THREE.InstancedMesh(
        sourceMesh.geometry,
        sourceMesh.material,
        objects.length
      )
  );

  // Set instance matrix data
  objects.forEach((object, index) => {
    // Remove the first grass billboard instance from the scene

    object.removeFromParent(object);
    const matrix = new THREE.Matrix4();
    matrix.compose(object.position, object.quaternion, object.scale);
    instancedObjects.forEach((instancedObject) =>
      instancedObject.setMatrixAt(index, matrix)
    );
  });

  // Update instance matrix buffer
  instancedObjects.forEach((instancedObject) => {
    instancedObject.instanceMatrix.needsUpdate = true;
    scene.add(instancedObject);
  });
};

export const renderProximalGrasses = (camera, grasses) => {
  // Render only grasses proximal to camera
  grasses.forEach((grass) => {
    const distance = camera.position.distanceTo(grass.position);
    grass.visible = distance < RENDER_CONSTANTS.VISIBILITY_THRESHOLD;
  });
};

/**
 * Processes terrain model and returns colliders and grasses
 * @param {*} terrain terrain model
 * @returns Array containing references to collider and grass objects
 */
export const processTerrainModel = (terrain, scene) => {
  let colliders = null;
  const instancedObjects = {};

  terrain.scene.traverse(function (child) {
    // Identify if child is an instanced object (Prefix of INST and suffix of _ + number)
    if (child.name.match(/INST\w+_\d+/)) {
      // Get the name of the cloned object without the numbers
      const name = child.name.substr(0, child.name.lastIndexOf("_"));

      if (!instancedObjects[name]) {
        instancedObjects[name] = [];
      }
      instancedObjects[name].push(child);
    }

    if (child.name === "colliders") {
      // clone colliders so we can remove grasses from it
      colliders = child.clone();
    } else if (child.name.includes("ground")) {
      setReceiveShadow(child, true);
      setCastShadow(child, true);
    } else if (child.name.includes("tree")) {
      // Any configuration specific to trees goes here
    } else if (child.name === "boundary") {
      // make boundary collider invisible
      child.visible = false;
    } else if (isGrass(child)) {
      setCastShadow(child, false);
      setReceiveShadow(child, false);
    } else if (child.name === "water") {
      child.layers.set(LAYERS.MINI_MAP_ONLY);
      // Fixes issue with water rendering over other objects
      // Disabled while water shader is in use
      // child.renderOrder = -1;
    }
  });
  Object.keys(instancedObjects).forEach((name) => {
    // get the first instance of the object, the one without the numbers suffix
    const firstInstance = terrain.scene.getObjectByName(name);
    if (firstInstance) {
      instancedObjects[name].push(firstInstance);
    }
    optimizeInstancedObjects(scene, instancedObjects[name]);
  });

  return colliders;
};
